import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faPlus, faEdit, faTrash, faSave, faTimes, faSpinner,
  faQuestionCircle, faCheckCircle, faBook, faGraduationCap, faUpload, faInfoCircle, faSearch,
  faRobot, faMagic
} from '@fortawesome/free-solid-svg-icons';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import MarkdownRenderer from '../components/MarkdownRenderer';

interface Question {
  id: number;
  exam_id: number;
  question_text: string;
  options: { [key: string]: string };
  correct_answer_key?: string;
  correct_answer_keys?: string[];
  explanation?: string;
  is_free_tier: boolean;
  topic?: string;
  difficulty?: number;
  created_at: string;
  ai_generated_explanation?: boolean;
  ai_generated_difficulty?: boolean;
}

interface Exam {
  id: number;
  name: string;
  description?: string;
}

const AdminQuestions: React.FC = () => {
  const { t } = useTranslation();
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [exam, setExam] = useState<Exam | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkImportText, setBulkImportText] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);

  // Form state
  const [formData, setFormData] = useState({
    question_text: '',
    options: { A: '', B: '', C: '', D: '' } as { [key: string]: string },
    correct_answer_keys: [] as string[],
    explanation: '',
    is_free_tier: false,
    topic: '',
    difficulty: 3
  });

  useEffect(() => {
    if (examId) {
      fetchData();
    }
  }, [examId]);

  const fetchData = async () => {
    try {
      const [examRes, questionsRes] = await Promise.all([
        api.get(`/exams/${examId}`),
        api.get(`/exams/${examId}/questions/admin`)
      ]);

      setExam(examRes.data.exam);
      setQuestions(questionsRes.data.questions || []);
    } catch (error: any) {
      console.error('Failed to fetch data:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to load questions';
      const details = error.response?.data?.details || '';
      alert(`${errorMessage}${details ? `: ${details}` : ''}. ${error.response?.status === 403 ? 'You may not have admin access.' : ''}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      question_text: '',
      options: { A: '', B: '', C: '', D: '' },
      correct_answer_keys: [],
      explanation: '',
      is_free_tier: false,
      topic: '',
      difficulty: 3
    });
    setShowAddForm(false);
    setEditingId(null);
  };

  const handleEdit = (question: Question) => {
    console.log('Editing question:', question);
    const correctAnswers = question.correct_answer_keys || 
      (question.correct_answer_key ? [question.correct_answer_key] : []);
    
    // Ensure options object has at least A, B, C, D keys
    const optionsWithDefaults = { A: '', B: '', C: '', D: '', ...question.options };
    
    const newFormData = {
      question_text: question.question_text,
      options: optionsWithDefaults,
      correct_answer_keys: correctAnswers,
      explanation: question.explanation || '',
      is_free_tier: question.is_free_tier,
      topic: question.topic || '',
      difficulty: question.difficulty || 3,
      ai_generated_difficulty: question.ai_generated_difficulty || false
    };
    
    console.log('Setting form data:', newFormData);
    setFormData(newFormData);
    setEditingId(question.id);
    setShowAddForm(false);
    
    // Scroll to form after a brief delay to ensure it's rendered
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        console.warn('Form ref not found');
      }
    }, 100);
  };

  const handleAddOption = () => {
    const optionKeys = Object.keys(formData.options);
    const nextKey = String.fromCharCode(optionKeys[optionKeys.length - 1].charCodeAt(0) + 1);
    if (nextKey <= 'Z') {
      setFormData({
        ...formData,
        options: { ...formData.options, [nextKey]: '' }
      });
    }
  };

  const handleRemoveOption = (key: string) => {
    if (Object.keys(formData.options).length <= 2) {
      alert('At least 2 options are required');
      return;
    }
    const newOptions = { ...formData.options };
    delete newOptions[key];
    
    // Remove from correct answers if selected
    const newCorrectAnswers = formData.correct_answer_keys.filter(ans => ans !== key);
    
    setFormData({
      ...formData,
      options: newOptions,
      correct_answer_keys: newCorrectAnswers
    });
  };

  const handleToggleCorrectAnswer = (key: string) => {
    const currentAnswers = formData.correct_answer_keys;
    if (currentAnswers.includes(key)) {
      if (currentAnswers.length === 1) {
        alert('At least one correct answer is required');
        return;
      }
      setFormData({
        ...formData,
        correct_answer_keys: currentAnswers.filter(ans => ans !== key)
      });
    } else {
      setFormData({
        ...formData,
        correct_answer_keys: [...currentAnswers, key].sort()
      });
    }
  };

  // Handle Ctrl+B (or Cmd+B on Mac) for bold markdown formatting
  const handleBoldShortcut = (
    e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
    field: 'question_text' | 'explanation' | `options.${string}`,
    currentValue: string
  ) => {
    // Check for Ctrl+B (Windows/Linux) or Cmd+B (Mac)
    if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
      e.preventDefault();
      
      const target = e.currentTarget as HTMLTextAreaElement | HTMLInputElement;
      const start = target.selectionStart ?? 0;
      const end = target.selectionEnd ?? 0;
      const selectedText = currentValue.substring(start, end);
      
      let newValue: string;
      let newCursorPos: number;
      
      if (selectedText) {
        // Wrap selected text with **
        newValue = currentValue.substring(0, start) + `**${selectedText}**` + currentValue.substring(end);
        newCursorPos = start + selectedText.length + 4; // Position after **text**
      } else {
        // Insert ** at cursor position
        newValue = currentValue.substring(0, start) + '****' + currentValue.substring(end);
        newCursorPos = start + 2; // Position between the **
      }
      
      // Update the appropriate field
      if (field === 'question_text') {
        setFormData({ ...formData, question_text: newValue });
      } else if (field === 'explanation') {
        setFormData({ ...formData, explanation: newValue });
      } else if (field.startsWith('options.')) {
        const optionKey = field.split('.')[1];
        setFormData({
          ...formData,
          options: { ...formData.options, [optionKey]: newValue }
        });
      }
      
      // Restore cursor position after state update
      setTimeout(() => {
        target.setSelectionRange(newCursorPos, newCursorPos);
        target.focus();
      }, 0);
    }
  };

  const validateForm = () => {
    if (!formData.question_text.trim()) {
      alert('Question text is required');
      return false;
    }
    
    const filledOptions = Object.entries(formData.options).filter(([_, value]) => value.trim());
    if (filledOptions.length < 2) {
      alert('At least 2 options are required');
      return false;
    }

    if (formData.correct_answer_keys.length === 0) {
      alert('At least one correct answer must be selected');
      return false;
    }

    // Validate all correct answers have filled options
    const invalidAnswers = formData.correct_answer_keys.filter(
      key => !formData.options[key] || !formData.options[key].trim()
    );
    if (invalidAnswers.length > 0) {
      alert(`Correct answers must have option text: ${invalidAnswers.join(', ')}`);
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      // Only include filled options
      const filledOptions: { [key: string]: string } = {};
      Object.entries(formData.options).forEach(([key, value]) => {
        if (value.trim()) {
          filledOptions[key] = value.trim();
        }
      });

      const payload = {
        question_text: formData.question_text.trim(),
        options: filledOptions,
        correct_answer_keys: formData.correct_answer_keys,
        explanation: formData.explanation.trim() || null,
        is_free_tier: formData.is_free_tier,
        topic: formData.topic.trim() || null,
        difficulty: formData.difficulty || null
      };

      if (editingId) {
        await api.put(`/exams/questions/${editingId}`, payload);
      } else {
        await api.post(`/exams/${examId}/questions`, payload);
      }

      await fetchData();
      resetForm();
    } catch (error: any) {
      console.error('Failed to save question:', error);
      alert(error.response?.data?.error || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (questionId: number) => {
    if (!window.confirm('Are you sure you want to delete this question? This action cannot be undone.')) {
      return;
    }

    setDeleting(questionId);
    try {
      await api.delete(`/exams/questions/${questionId}`);
      await fetchData();
    } catch (error) {
      console.error('Failed to delete question:', error);
      alert('Failed to delete question');
    } finally {
      setDeleting(null);
    }
  };

  const handleBulkImport = async () => {
    if (!bulkImportText.trim()) {
      alert('Please paste JSON questions array');
      return;
    }

    setBulkImporting(true);
    setImportResult(null);

    try {
      // Parse JSON
      let questions;
      try {
        questions = JSON.parse(bulkImportText);
      } catch (error) {
        setImportResult({
          success: false,
          error: 'Invalid JSON format. Please check your syntax.'
        });
        return;
      }

      if (!Array.isArray(questions)) {
        setImportResult({
          success: false,
          error: 'Input must be a JSON array of questions'
        });
        return;
      }

      if (questions.length === 0) {
        setImportResult({
          success: false,
          error: 'Questions array is empty'
        });
        return;
      }

      // Send to API
      const response = await api.post(`/exams/${examId}/questions/bulk`, { questions });
      
      setImportResult({
        success: true,
        ...response.data
      });

      // Refresh questions list if any were imported
      if (response.data.imported > 0) {
        await fetchData();
      }
    } catch (error: any) {
      console.error('Bulk import error:', error);
      setImportResult({
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to import questions'
      });
    } finally {
      setBulkImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="gp-page flex items-center justify-center">
        <div className="text-center">
          <div className="gp-spinner mb-3" aria-hidden="true" />
          <p className="gp-text-muted">Loading questions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gp-page py-6">
      <div className="gp-container">
        {/* Header */}
        <div className="mb-5">
          <Link
            to="/dashboard"
            className="gp-back-link mb-3"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2 text-xs" />
            {t('common.backToDashboard')}
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
                <div className="flex items-center gap-2.5 mb-1">
                <span className="gp-icon-tile-lg">
                  <FontAwesomeIcon icon={faQuestionCircle} className="text-lg" aria-hidden="true" />
                </span>
                <div>
                  <h1 className="gp-page-title">Question Management</h1>
                  <p className="gp-page-subtitle">
                    {exam?.name} • {questions.length} question{questions.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <Link
                to="/admin/question"
                className="gp-btn-ghost flex items-center gap-2 px-4 py-2 text-sm"
              >
                <FontAwesomeIcon icon={faSearch} className="text-xs" />
                <span>Search Question</span>
              </Link>
              <button
                onClick={() => {
                  setShowBulkImport(true);
                  setImportResult(null);
                  setBulkImportText('');
                }}
                className="gp-btn-ghost flex items-center gap-2 px-4 py-2 text-sm"
              >
                <FontAwesomeIcon icon={faUpload} className="text-xs" />
                <span>Bulk Import</span>
              </button>
              <button
                onClick={() => {
                  resetForm();
                  setShowAddForm(true);
                }}
                className="gp-btn-primary flex items-center gap-2 px-4 py-2 text-sm"
              >
                <FontAwesomeIcon icon={faPlus} className="text-xs" />
                <span>Add Question</span>
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Import Section */}
        {showBulkImport && (
          <div className="gp-card mb-5">
            <div className="flex items-center justify-between mb-4 pb-4 border-b">
              <div className="flex items-center gap-2.5">
                <FontAwesomeIcon icon={faUpload} style={{ color: 'var(--gp-brand)' }} aria-hidden="true" />
                <h2 className="gp-heading-lg text-lg">
                  Bulk Import Questions
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowBulkImport(false);
                  setBulkImportText('');
                  setImportResult(null);
                }}
                className="gp-text-muted"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Instructions */}
            <div className="mb-5 p-4 gp-soft-panel rounded-lg">
              <div className="flex items-start gap-2.5">
                <FontAwesomeIcon icon={faInfoCircle} className="mt-1" />
                <div className="flex-1">
                  <h3 className="font-semibold mb-2">How to Format Questions for Bulk Import</h3>
                  <p className="text-sm gp-text-muted mb-3">
                    Paste a JSON array of questions. Each question must follow this format:
                  </p>
                  <pre className="p-3 rounded text-xs overflow-x-auto" style={{ background: 'var(--gp-surface-muted)', border: '1px solid var(--gp-border)', color: 'var(--gp-ink)' }}>
{`[
  {
    "question_text": "What is AWS?",
    "options": {
      "A": "Amazon Web Services",
      "B": "Amazon Workflow System",
      "C": "Amazon Web System",
      "D": "Amazon Work Services"
    },
    "correct_answer_keys": ["A"],
    "explanation": "AWS stands for Amazon Web Services.",
    "is_free_tier": true,
    "topic": "AWS Basics",
    "difficulty": 1
  },
  {
    "question_text": "Which service is used for compute?",
    "options": {
      "A": "S3",
      "B": "EC2",
      "C": "RDS",
      "D": "Lambda"
    },
    "correct_answer_keys": ["B"],
    "explanation": "EC2 provides virtual servers.",
    "is_free_tier": false,
    "topic": "Compute",
    "difficulty": 2
  }
]`}
                  </pre>
                  <div className="mt-3 text-xs gp-text-muted space-y-1">
                    <p><strong>Required fields:</strong> question_text, options, correct_answer_keys</p>
                    <p><strong>Optional fields:</strong> explanation, is_free_tier (default: false), topic, difficulty (1-5)</p>
                    <p><strong>Options:</strong> Must be an object with keys A, B, C, D (or more). Each value is the option text.</p>
                    <p><strong>Correct answers:</strong> Array of option keys (e.g., ["A"] or ["A", "B"] for multiple correct answers).</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Text area for JSON input */}
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">
                JSON Questions Array
              </label>
              <textarea
                value={bulkImportText}
                onChange={(e) => setBulkImportText(e.target.value)}
                className="input-field font-mono"
                rows={15}
                placeholder="Paste your JSON array of questions here..."
              />
            </div>

            {/* Import Results */}
            {importResult && (
              <div
                className="mb-4 p-4 rounded-md"
                style={
                  importResult.success
                    ? { background: 'var(--gp-success-soft)', border: '1px solid var(--gp-success)', color: 'var(--gp-success)' }
                    : { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c' }
                }
              >
                <div className="flex items-start gap-2">
                  <FontAwesomeIcon
                    icon={importResult.success ? faCheckCircle : faTimes}
                    className="mt-1"
                    aria-hidden="true"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold mb-2">
                      Import {importResult.success ? 'Successful' : 'Failed'}
                    </h4>
                    {importResult.success && (
                      <div className="text-sm space-y-1">
                        <p>✓ Imported: {importResult.imported} questions</p>
                        {importResult.failed > 0 && (
                          <p style={{ color: 'var(--gp-accent)' }}>
                            ⚠ Failed: {importResult.failed} questions
                          </p>
                        )}
                        {importResult.details?.failed?.length > 0 && (
                          <div className="mt-2">
                            <p className="font-semibold">Failed Questions:</p>
                            <ul className="list-disc list-inside space-y-1">
                              {importResult.details.failed.map((f: any) => (
                                <li key={f.index}>Question {f.index}: {f.error}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                    {!importResult.success && (
                      <p className="text-sm">
                        {importResult.error || 'Failed to import questions'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-2.5 pt-4 border-t">
              <button
                onClick={() => {
                  setShowBulkImport(false);
                  setBulkImportText('');
                  setImportResult(null);
                }}
                className="gp-btn-ghost text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkImport}
                disabled={bulkImporting || !bulkImportText.trim()}
                className="gp-btn-primary flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bulkImporting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
                    <span>Importing...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faUpload} className="text-xs" />
                    <span>Import Questions</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Add/Edit Form */}
        {(showAddForm || editingId !== null) && (
          <div ref={formRef} className="gp-card mb-5 animate-fade-in">
            <div className="flex items-center justify-between mb-4 pb-4 border-b">
              <div className="flex items-center gap-2.5">
                <h2 className="text-lg font-bold">
                  {editingId ? 'Edit Question' : 'Add New Question'}
                </h2>
                {editingId && (
                  <span className="px-2 py-1 rounded text-xs font-semibold">
                    ID: {editingId}
                  </span>
                )}
              </div>
              <button
                onClick={resetForm}
                className="gp-text-muted"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="space-y-3">
              {/* Markdown Help */}
              <div className="p-3 gp-soft-panel rounded-lg">
                <div className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faInfoCircle} className="mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold mb-1">Markdown Formatting Supported</p>
                    <p className="text-xs gp-text-muted mb-1">
                      Use <code className="px-1 rounded" style={{ background: 'var(--gp-surface-muted)' }}>**bold**</code> (or <kbd className="px-1.5 py-0.5 rounded text-xs font-mono font-semibold" style={{ background: 'var(--gp-surface-muted)', border: '1px solid var(--gp-border)' }}>Ctrl+B</kbd>), <code className="px-1 rounded" style={{ background: 'var(--gp-surface-muted)' }}>*italic*</code>, <code className="px-1 rounded" style={{ background: 'var(--gp-surface-muted)' }}>`code`</code>, or double newlines for paragraphs.
                    </p>
                    <p className="text-xs gp-text-muted mb-1">
                      Headings: <code className="px-1 rounded" style={{ background: 'var(--gp-surface-muted)' }}># H1</code>, <code className="px-1 rounded" style={{ background: 'var(--gp-surface-muted)' }}>## H2</code>, <code className="px-1 rounded" style={{ background: 'var(--gp-surface-muted)' }}>### H3</code>
                    </p>
                    <p className="text-xs gp-text-muted">
                      Lists: <code className="px-1 rounded" style={{ background: 'var(--gp-surface-muted)' }}>- item</code> or <code className="px-1 rounded" style={{ background: 'var(--gp-surface-muted)' }}>* item</code> for unordered, <code className="px-1 rounded" style={{ background: 'var(--gp-surface-muted)' }}>1. item</code> for ordered. Preview updates as you type.
                    </p>
                  </div>
                </div>
              </div>

              {/* Question Text */}
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Question Text *
                </label>
                <textarea
                  value={formData.question_text}
                  onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                  onKeyDown={(e) => handleBoldShortcut(e, 'question_text', formData.question_text)}
                  className="input-field"
                  rows={3}
                  placeholder="Enter the question text..."
                />
                {formData.question_text && (
                  <div className="mt-2 p-3 rounded-lg border">
                    <p className="text-xs font-semibold gp-text-muted mb-2">Preview:</p>
                    <div className="text-sm font-medium">
                      <MarkdownRenderer content={formData.question_text} />
                    </div>
                  </div>
                )}
              </div>

              {/* Options */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold">
                    Options *
                  </label>
                  {Object.keys(formData.options).length < 6 && (
                    <button
                      type="button"
                      onClick={handleAddOption}
                      className="text-xs gp-link"
                    >
                      + Add Option
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {Object.entries(formData.options).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded-md flex items-center justify-center font-bold text-sm flex-shrink-0"
                        style={{ background: 'var(--gp-surface-muted)', color: 'var(--gp-ink)' }}
                      >
                        {key}
                      </div>
                      <div className="flex-1 flex flex-col">
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              options: { ...formData.options, [key]: e.target.value }
                            });
                          }}
                          onKeyDown={(e) => handleBoldShortcut(e, `options.${key}`, value)}
                          className="input-field"
                          placeholder={`Option ${key} text...`}
                        />
                        {value && (
                          <div className="mt-1 p-2 rounded-md" style={{ background: 'var(--gp-surface-muted)', border: '1px solid var(--gp-border)' }}>
                            <p className="text-xs gp-text-muted mb-1">Preview:</p>
                            <div className="text-xs">
                              <MarkdownRenderer content={value} />
                            </div>
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleToggleCorrectAnswer(key)}
                        className="w-10 h-10 rounded-md flex items-center justify-center transition-all"
                        style={
                          formData.correct_answer_keys.includes(key)
                            ? { background: 'var(--gp-success)', color: '#fff' }
                            : { background: 'var(--gp-surface-muted)', color: 'var(--gp-ink-muted)' }
                        }
                        title={formData.correct_answer_keys.includes(key) ? 'Correct Answer' : 'Mark as Correct'}
                      >
                        <FontAwesomeIcon icon={faCheckCircle} className="text-sm" aria-hidden="true" />
                      </button>
                      {Object.keys(formData.options).length > 2 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveOption(key)}
                          className="w-10 h-10 rounded-md flex items-center justify-center"
                          style={{ background: '#fef2f2', color: '#b91c1c' }}
                        >
                          <FontAwesomeIcon icon={faTimes} className="text-xs" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs gp-text-muted mt-2">
                  Click the checkmark to mark options as correct (multiple answers allowed)
                </p>
              </div>

              {/* Correct Answers Display */}
              {formData.correct_answer_keys.length > 0 && (
                <div
                  className="p-3 rounded-md"
                  style={{ background: 'var(--gp-success-soft)', border: '1px solid var(--gp-success)' }}
                >
                  <p className="text-xs font-semibold mb-1" style={{ color: 'var(--gp-success)' }}>
                    Correct Answer{formData.correct_answer_keys.length > 1 ? 's' : ''}:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {formData.correct_answer_keys.map(key => (
                      <span
                        key={key}
                        className="px-2 py-1 rounded text-xs font-semibold"
                        style={{ background: 'var(--gp-success-soft)', color: 'var(--gp-success)' }}
                      >
                        {key}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Explanation */}
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Explanation
                </label>
                <textarea
                  value={formData.explanation}
                  onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
                  onKeyDown={(e) => handleBoldShortcut(e, 'explanation', formData.explanation)}
                  className="input-field"
                  rows={2}
                  placeholder="Enter explanation for the correct answer(s)..."
                />
                {formData.explanation && (
                  <div className="mt-2 p-3 rounded-lg border">
                    <p className="text-xs font-semibold gp-text-muted mb-2">Preview:</p>
                    <div className="text-sm">
                      <MarkdownRenderer content={formData.explanation} />
                    </div>
                  </div>
                )}
              </div>

              {/* Additional Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-3">
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Topic
                  </label>
                  <input
                    type="text"
                    value={formData.topic}
                    onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                    className="input-field"
                    placeholder="e.g., Networking"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Difficulty (1-5)
                  </label>
                  <input
                    type="number"
                    value={formData.difficulty}
                    onChange={(e) => setFormData({ ...formData, difficulty: parseInt(e.target.value) || 3 })}
                    className="input-field"
                    min="1"
                    max="5"
                  />
                </div>

                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_free_tier}
                      onChange={(e) => setFormData({ ...formData, is_free_tier: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm font-semibold">
                      Free Tier Available
                    </span>
                  </label>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-2.5 pt-4 border-t">
                <button
                  onClick={resetForm}
                  className="gp-btn-ghost text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="gp-btn-primary flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="text-xs" />
                      <span>Save Question</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Questions List */}
        <div className="gp-card">
          {questions.length === 0 ? (
            <div className="text-center py-12">
              <FontAwesomeIcon icon={faQuestionCircle} className="text-4xl gp-text-muted mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Questions Yet</h3>
              <p className="text-sm gp-text-muted mb-4">
                Get started by adding your first question
              </p>
              <button
                onClick={() => {
                  resetForm();
                  setShowAddForm(true);
                }}
                className="gp-btn-primary inline-flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faPlus} className="text-xs" />
                <span>Add First Question</span>
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {questions.map((question) => {
                const correctAnswers = question.correct_answer_keys || 
                  (question.correct_answer_key ? [question.correct_answer_key] : []);
                const isEditing = editingId === question.id;

                return (
                  <div
                    key={question.id}
                    className={`p-3.5 rounded-md transition-all ${ isEditing ? 'gp-soft-panel' : '' }`}
                    style={{ border: isEditing ? '1px solid var(--gp-brand)' : '1px solid var(--gp-border)' }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-semibold gp-text-muted">
                            ID: {question.id}
                          </span>
                          {question.topic && (
                            <>
                              <span className="gp-text-muted">•</span>
                              <span
                                className="px-2 py-0.5 rounded text-xs"
                                style={{ background: 'var(--gp-surface-muted)', color: 'var(--gp-ink-muted)' }}
                              >
                                {question.topic}
                              </span>
                            </>
                          )}
                          {question.difficulty && (
                            <>
                              <span className="gp-text-muted">•</span>
                              <span className="text-xs gp-text-muted flex items-center gap-1">
                                <span>{'★'.repeat(question.difficulty)}{'☆'.repeat(5 - question.difficulty)}</span>
                                {question.ai_generated_difficulty && (
                                  <span className="ml-1" title="AI-Generated Difficulty">
                                    <FontAwesomeIcon icon={faRobot} className="text-[10px]" style={{ color: 'var(--gp-ai)' }} aria-hidden="true" />
                                  </span>
                                )}
                              </span>
                            </>
                          )}
                          {question.is_free_tier && (
                            <span className="gp-tag-free">Free</span>
                          )}
                        </div>
                        <div className="text-sm font-medium mb-3" style={{ color: 'var(--gp-ink)' }}>
                          <MarkdownRenderer content={question.question_text} />
                        </div>
                        <div className="space-y-1.5 mb-3">
                          {Object.entries(question.options).map(([key, value]) => (
                            <div
                              key={key}
                              className={`flex items-center gap-2 text-xs ${correctAnswers.includes(key) ? 'font-semibold' : 'gp-text-muted'}`}
                              style={correctAnswers.includes(key) ? { color: 'var(--gp-success)' } : undefined}
                            >
                              <span
                                className="w-6 h-6 rounded flex items-center justify-center font-bold"
                                style={
                                  correctAnswers.includes(key)
                                    ? { background: 'var(--gp-success-soft)', color: 'var(--gp-success)' }
                                    : { background: 'var(--gp-surface-muted)', color: 'var(--gp-ink-muted)' }
                                }
                              >
                                {key}
                              </span>
                              <span><MarkdownRenderer content={value} /></span>
                              {correctAnswers.includes(key) && (
                                <FontAwesomeIcon icon={faCheckCircle} className="text-xs" style={{ color: 'var(--gp-success)' }} aria-hidden="true" />
                              )}
                            </div>
                          ))}
                        </div>
                        {question.explanation && (
                          <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--gp-border)' }}>
                            <p className="text-xs font-semibold mb-1 flex items-center gap-2" style={{ color: 'var(--gp-ink)' }}>
                              <span>Explanation:</span>
                              {question.ai_generated_explanation && (
                                <span
                                  className="flex items-center gap-1 gp-badge-ai text-[10px] px-1.5 py-0.5 rounded"
                                  title="AI-Generated Explanation"
                                >
                                  <FontAwesomeIcon icon={faRobot} className="text-[10px]" aria-hidden="true" />
                                  <span className="text-[10px]">AI</span>
                                </span>
                              )}
                            </p>
                            <div className="text-xs gp-text-muted">
                              <MarkdownRenderer content={question.explanation} />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Edit button clicked for question:', question.id);
                            handleEdit(question);
                          }}
                          className="gp-btn-primary text-xs"
                        >
                          <FontAwesomeIcon icon={faEdit} className="text-xs" aria-hidden="true" />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={() => handleDelete(question.id)}
                          disabled={deleting === question.id}
                          className="px-3 py-2 rounded-md text-xs font-medium transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ background: '#b91c1c', color: '#fff' }}
                        >
                          {deleting === question.id ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
                              <span>Deleting...</span>
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faTrash} className="text-xs" />
                              <span>Delete</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminQuestions;

